// ==========================================================================
// lib/tickets.ts — ticket data hooks (FF-004, 9 Oct 2026)
// ==========================================================================
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "./supabase";
import { useHouseholdId, useMe } from "./household";

export type TicketStatus = "open" | "doing" | "blocked" | "done";
export type TicketPriority = "P0" | "P1" | "P2" | "P3";
export type TicketType = "new" | "fix" | "doc";
export type TicketArea = "core" | "logs" | "life" | "money" | "wealth" | "tax";

export type Ticket = {
  id: string; ref: string; title: string; body: string;
  type: TicketType; priority: TicketPriority; status: TicketStatus; area: TicketArea;
  done_on: string | null; created_at: string; updated_at: string;
};
export type TicketDraft = Pick<Ticket, "title" | "body" | "type" | "priority" | "status" | "area">;

export const STATUSES: { key: TicketStatus; label: string }[] = [
  { key: "doing", label: "In progress" }, { key: "open", label: "Open" }, { key: "blocked", label: "Blocked" }, { key: "done", label: "Done" },
];
export const PRIORITIES: { key: TicketPriority; label: string; hint: string }[] = [
  { key: "P0", label: "P0", hint: "Blocks use or puts data at risk" },
  { key: "P1", label: "P1", hint: "Do next" },
  { key: "P2", label: "P2", hint: "Wanted for the first version" },
  { key: "P3", label: "P3", hint: "Later" },
];
export const TYPES: { key: TicketType; label: string }[] = [{ key: "new", label: "New" }, { key: "fix", label: "Fix" }, { key: "doc", label: "Decision" }];
export const AREAS: { key: TicketArea; label: string }[] = [
  { key: "core", label: "Core" }, { key: "logs", label: "Logs" }, { key: "life", label: "Family life" },
  { key: "money", label: "Money" }, { key: "wealth", label: "Wealth" }, { key: "tax", label: "Tax" },
];

const PRIORITY_ORDER: Record<TicketPriority, number> = { P0: 0, P1: 1, P2: 2, P3: 3 };

export function useTickets() {
  const hid = useHouseholdId();
  return useQuery({
    queryKey: ["tickets", hid], enabled: !!hid,
    queryFn: async () => {
      const { data, error } = await supabase.from("tickets").select("*").eq("household_id", hid).is("deleted_at", null);
      if (error) throw error;
      return (data as Ticket[]).sort((a, b) => PRIORITY_ORDER[a.priority] - PRIORITY_ORDER[b.priority] || b.updated_at.localeCompare(a.updated_at));
    },
  });
}

export function useSaveTicket() {
  const hid = useHouseholdId();
  const me = useMe();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, draft }: { id?: string; draft: TicketDraft }) => {
      if (id) {
        const { error } = await supabase.from("tickets").update(draft).eq("id", id);
        if (error) throw error;
        return;
      }
      const { data: ref, error: refErr } = await supabase.rpc("next_ticket_ref", { p_household_id: hid });
      if (refErr) throw refErr;
      const { error } = await supabase.from("tickets").insert({ ...draft, household_id: hid, ref, created_by: me?.id ?? null });
      if (error) throw error;
    },
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ["tickets", hid] }); void qc.invalidateQueries({ queryKey: ["audit", hid] }); },
  });
}
